import { useCallback } from "react";
import axios from "axios";
import { composeApiURL } from "helpers/utility/axiosHelpers";
import { downloadFile } from "../../helpers/utility/downloadFile";
import { useFlywheelDatasetCurrentStatusApiV1FlywheelDatasetIdGet } from "../../vl-api/vlComponents";

interface FlywheelAnnotationItem {
  image_path: string;
  label_name: string;
  source: string;
}

interface FlywheelAnnotationsExportResponse {
  dataset_id: string;
  timestamp: string;
  total_annotations: number;
  annotations: FlywheelAnnotationItem[];
}

export const useLabelPropagationExport = (datasetId?: string) => {
  const { data, refetch } =
    useFlywheelDatasetCurrentStatusApiV1FlywheelDatasetIdGet(
      { pathParams: { datasetId: String(datasetId) } },
      { enabled: Boolean(datasetId) },
    );

  const isExportLabelPropagation = data?.is_exportable;

  const HOST_URL = process.env.REACT_APP_API_ENDPOINT!;

  const exportLabelPropagation = useCallback(
    async (fileName: string) => {
      if (!isExportLabelPropagation) {
        throw new Error("Label propagation is not ready to export");
      }

      try {
        const url = composeApiURL(
          HOST_URL,
          `/flywheel/${datasetId}/annotations/latest`,
        );
        const response =
          await axios.get<FlywheelAnnotationsExportResponse>(url);
        const fetchedAnnotations = response.data;

        if (fetchedAnnotations.total_annotations === 0) {
          throw new Error("No data available for export");
        }

        // Convert annotations to JSON string
        const jsonString = JSON.stringify(fetchedAnnotations, null, 2);

        // Create and trigger download
        const blob = new Blob([jsonString], { type: "application/json" });
        downloadFile(`${fileName}.json`, { type: "data", data: blob });
      } catch (error) {
        console.error("Error downloading annotations:", error);
        throw error;
      }
    },
    [isExportLabelPropagation, HOST_URL, datasetId],
  );

  return {
    isExportLabelPropagation,
    refetch,
    exportLabelPropagation,
  };
};
